import { useCallback } from "react";
import type { Routes } from "safe-routes";
import { $path } from "safe-routes";
import { useOptionalRootContext } from "../auth/hooks";
import type {
  EmptyShortenedParams,
  OmitLabelArtistParams,
  RouteMapping,
  ShortenedRoutes,
} from "./types";

export function useContextPath() {
  const context = useOptionalRootContext();

  /**
   * function that extends the $path function to include the context path for the current label and
   * artist. When using the for /labels/:labelId/artists/:artistId/media, the function will
   * automatically add the label and artist id to the path, and the path to pass as the parameter
   * will be /media.
   *
   * @param route The route to navigate to
   * @param routeParams The params for the route, if any are required. For example for /media/:mediaId,
   * the routeParams would be { mediaId: "123" }
   * @param query The query parameters for the route
   *
   * @returns The path to navigate to
   */
  const contextPath = useCallback(
    function contextPath<
      Route extends ShortenedRoutes,
      Rest extends {
        params?: OmitLabelArtistParams<Routes[RouteMapping[Route]]["params"]>;
        query?: Routes[RouteMapping[Route]]["query"];
      },
    >(
      ...args: Rest["params"] extends EmptyShortenedParams<Rest["params"]>
        ? [route: Route, query?: Rest["query"]]
        : [route: Route, params: Rest["params"], query?: Rest["query"]]
    ) {
      const [route, ...otherArgs] = args;
      const prefix = context
        ? `/labels/${context?.label?.uid}/artists/${
            context?.artist?.uid ?? "all"
          }`
        : "";
      const fullPath = `${prefix}${route}`;

      return $path(
        ...([fullPath as Route, ...otherArgs] as unknown as Parameters<
          typeof $path
        >),
      );
    },
    [context],
  );

  return contextPath;
}
